/* eslint-disable valid-jsdoc */
import { ref, type Ref } from "vue";
import {
  WebSocketClient,
  type WebSocketClientOptions,
  type WebSocketEvent,
  type SendData,
} from "./WebSocketClient";

/**
 * 全局 WebSocket 管理器（单例）
 * 整个应用只维护一个连接，所有组件共享
 */
export class WebSocketManager {
  private static instance: WebSocketManager;
  private client: WebSocketClient | null = null;
  private options: WebSocketClientOptions | null = null;

  // 全局响应式连接状态（可注入到组件中使用）
  public isConnected: Ref<boolean> = ref(false);

  // 重连尝试次数计数器
  private reconnectAttempts = 0;

  // 管理器级别的全局事件监听器集合（用于组件全局订阅，无需每个组件单独注册客户端事件）
  private globalListeners: Map<
    WebSocketEvent,
    Array<(...args: any[]) => void>
  > = new Map();

  private constructor() {
    // 初始化所有事件的监听器数组
    const events: WebSocketEvent[] = [
      "open",
      "close",
      "message",
      "error",
      "reconnect",
    ];
    events.forEach((ev) => this.globalListeners.set(ev, []));
  }

  public static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  /**
   * 初始化连接（应用启动时调用一次）
   * @param options - 连接配置
   * @returns WebSocketClient 实例
   */
  public init(options: WebSocketClientOptions): WebSocketClient {
    if (this.client) {
      console.warn("[WebSocketManager] 连接已存在，先销毁旧连接");
      this.destroy();
    }
    this.options = options;
    this.client = new WebSocketClient(options);
    this.reconnectAttempts = 0;

    // 绑定客户端原生事件，同步全局状态并触发管理器全局监听器
    this.client.on("open", (event) => {
      this.isConnected.value = true;
      this.reconnectAttempts = 0;
      this._invokeGlobalListeners("open", event);
    });
    this.client.on("close", (event) => {
      this.isConnected.value = false;
      this._invokeGlobalListeners("close", event);
    });
    this.client.on("error", (error) => {
      this.isConnected.value = false;
      this._invokeGlobalListeners("error", error);
    });
    this.client.on("message", (data) => {
      this._invokeGlobalListeners("message", data);
    });
    this.client.on("reconnect", () => {
      this.reconnectAttempts++;
      this._invokeGlobalListeners("reconnect", this.reconnectAttempts);
    });

    // 自动连接（不阻塞，异步进行）
    this.client.connect().catch((err) => {
      console.error("[WebSocketManager] 初始连接失败", err);
      this.isConnected.value = false;
    });

    return this.client;
  }

  /**
   * 获取原始 WebSocketClient 实例（仅在需要底层控制时使用）
   */
  public getClient(): WebSocketClient | null {
    return this.client;
  }

  // ========== 便捷发送方法 ==========

  /**
   * 发送消息（自动判断连接状态）
   * @param message - 消息内容（字符串或 ArrayBuffer）
   * @param hexMode - 是否为十六进制模式（仅对字符串有效）
   * @returns 是否发送成功
   */
  public send(message: string | ArrayBuffer, hexMode = false): boolean {
    if (!this.client || !this.client.isConnected()) {
      console.warn("[WebSocketManager] 连接未就绪，无法发送消息");
      return false;
    }
    return this.client.send(message, hexMode);
  }

  /**
   * 发送文本消息
   * @param sendData - 发送的数据对象
   * @returns 是否发送成功
   */
  public sendText(sendData: SendData): boolean {
    const data: SendData = {
      type: sendData.type, //转发形式 reg | msg
      toClientIp: sendData.toClientIp, //转发目标客户端
      forwardingType: sendData.forwardingType, //转发形式 txt | hex
      forwardingChannels: sendData.forwardingChannels, //转发渠道 ws | tcp | udp
      message: sendData.message, //消息内容
    };
    return this.send(JSON.stringify(data), false);
  }

  /**
   * 发送十六进制字符串（如 "48656c6c6f"）
   * @param hexString - 十六进制字符串
   * @returns 是否发送成功
   */
  public sendHex(hexString: string): boolean {
    return this.send(hexString, true);
  }

  /**
   * 发送二进制数据
   * @param data - 二进制数据
   * @returns 是否发送成功
   */
  public sendBinary(data: ArrayBuffer): boolean {
    return this.send(data, false);
  }

  // ========== 全局事件监听（管理器级别） ==========
  // 注意：这些全局监听器会一直存在，除非手动 off。适合做一些全局日志、弹窗等。
  // 组件若只想临时监听，建议直接使用 client.on/off，并在 onUnmounted 中清理。

  /**
   * 注册全局事件监听器（推荐用于跨组件的统一处理，如全局提示）
   * @param event - 事件名
   * @param callback - 回调函数
   */
  public onGlobal(
    event: WebSocketEvent,
    callback: (...args: any[]) => void
  ): void {
    const listeners = this.globalListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  /**
   * 移除全局事件监听器
   * @param event - 事件名
   * @param callback - 要移除的回调
   */
  public offGlobal(
    event: WebSocketEvent,
    callback: (...args: any[]) => void
  ): void {
    const listeners = this.globalListeners.get(event);
    if (listeners) {
      const idx = listeners.indexOf(callback);
      if (idx !== -1) {
        listeners.splice(idx, 1);
      }
    }
  }

  // 内部触发全局监听器
  private _invokeGlobalListeners(event: WebSocketEvent, ...args: any[]): void {
    const listeners = this.globalListeners.get(event);
    if (listeners) {
      listeners.forEach((cb) => cb(...args));
    }
  }

  // ========== 状态查询 ==========

  /**
   * 获取当前连接状态（同步布尔值）
   * @returns 当前是否已连接
   */
  public getConnectedStatus(): boolean {
    return this.isConnected.value;
  }

  /**
   * 获取当前配置（只读副本）
   * @returns 当前配置或 null
   */
  public getOptions(): Readonly<WebSocketClientOptions> | null {
    return this.options ? { ...this.options } : null;
  }

  /**
   * 获取重连尝试次数
   * @returns 重连尝试次数
   */
  public getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // ========== 连接控制 ==========

  /**
   * 主动断开连接（可选禁用自动重连）
   * @param disableAutoReconnect - 是否禁用自动重连，默认 true（即断开后不会自动重连）
   */
  public disconnect(disableAutoReconnect = true): void {
    if (this.client) {
      this.client.disconnect(disableAutoReconnect);
    }
    this.isConnected.value = false;
  }

  /**
   * 手动重连（可更换服务器地址）
   * @param options - 新配置，不传则使用原有配置
   * @returns Promise，等待连接成功或失败
   */
  public async reconnect(options?: WebSocketClientOptions): Promise<void> {
    if (this.client) {
      this.client.destroy();
    }
    const finalOptions = options || this.options;
    if (!finalOptions) {
      throw new Error("[WebSocketManager] 未提供连接配置，无法重连");
    }
    this.init(finalOptions);
    // 等待连接成功或超时（最多10秒）
    return this.waitForConnection(10000);
  }

  /**
   * 等待连接就绪（可用于在发送重要消息前确保连接已建立）
   * @param timeoutMs - 超时时间（毫秒），默认10000
   * @returns Promise，连接成功时 resolve，超时或失败则 reject
   */
  public waitForConnection(timeoutMs = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected.value) {
        resolve();
        return;
      }
      const startTime = Date.now();
      const interval = setInterval(() => {
        if (this.isConnected.value) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - startTime >= timeoutMs) {
          clearInterval(interval);
          reject(new Error(`等待 WebSocket 连接超时 (${timeoutMs}ms)`));
        }
      }, 100);
    });
  }

  /**
   * 完全销毁管理器（应用退出时调用，会清理所有全局监听器和客户端连接）
   */
  public destroy(): void {
    if (this.client) {
      this.client.destroy();
      this.client = null;
    }
    this.isConnected.value = false;
    this.options = null;
    this.reconnectAttempts = 0;
    // 清空所有全局监听器，避免内存泄漏
    this.globalListeners.clear();
    const events: WebSocketEvent[] = [
      "open",
      "close",
      "message",
      "error",
      "reconnect",
    ];
    events.forEach((ev) => this.globalListeners.set(ev, []));
  }
}
export default WebSocketManager;
